/**
 * exchangeSend.ts — the Exchange counter, sent for real.
 *
 * The last of the six controls. `#ex-send` sits inside the review modal that
 * `#ex-review` opens, and his handler fabricates the whole outcome: a 1.7s
 * success screen, then a history row stamped with a hardcoded "Aug 21 · 11:30
 * AM" and both sides of the thread rewritten from the deck, with no server call
 * anywhere. This does the POST instead and re-reads the row.
 *
 * 🔑 IT NOW SENDS BOTH, AND THEY ARE DIFFERENT DEALS. A fresh Exchange offer —
 * no `counterOf` — is a NEW offer. Until card 1218886330579015 it took refusal
 * 1 along with the reload, so the Exchange could not start a trade at all.
 * `sendFresh` takes it now; the counter path below is unchanged.
 *
 * ⚠️ REFUSAL 1 IS SPLIT, NOT DELETED, AND THE OLD COMMENT HAD THE MECHANISM
 * WRONG. It said a reload "rebuilds it with `counterOf: null`" and "the page
 * looks identical". Only the first half is true. `app.js:8008` rebuilds with
 * `exch = freshExch(r.handle)` and NO opts, so `mine`, `theirs`, `myCash` and
 * `theirCash` all come back EMPTY along with `counterOf`. A reloaded Exchange
 * is a blank one, not a filled one missing its chip.
 *
 * 🔑 That is what makes the split safe, and it is a property rather than a
 * promise: a fresh send needs an anchor, the anchor is one of THEIR items, and
 * a reload has no items. So the reload still cannot post — it is refused by
 * `NO_ANCHOR_MESSAGE` instead of by `counterOf` being null. The only way to
 * reach a real fresh send is to have deliberately picked something of theirs,
 * which is exactly the act that makes it a new offer.
 *
 * ── THE FIVE REFUSALS, AND WHY EACH IS A REFUSAL RATHER THAN A GUESS ────────
 *
 * 🔴 1. NO `counterOf` — THE RELOAD. `exch` is a plain module binding with no
 * persistence and the URL carries no parent, so reloading `#/exchange/<handle>`
 * mid-counter silently rebuilds it with `counterOf: null`. The page looks
 * identical and the COUNTER OFFER chip simply is not drawn. Sending then would
 * post a NEW offer, leaving the parent pending — a silent downgrade nobody
 * could see. Refused out loud.
 *
 * 🔴 2. THE PARENT OFFER ID WILL NOT RESOLVE. `counterOf` is a THREAD id; the
 * wire needs an offer id, and `__gbzThreadOfferId` answers null for a row whose
 * detail never loaded — and deliberately after a counter lands, so a stale one
 * cannot be reused. An unresolvable parent means the supersede would silently
 * not happen (the server logs and carries on, creating an orphan).
 *
 * 🔴 3. THE VIEWER'S SIDE IS UNKNOWN. `offeredItems`/`cashAmount` are the
 * canonical BUYER side whoever sends; the decks are mine/theirs from the
 * viewer's seat. Without `__gbzThreadIsBuyer` the two cannot be mapped, and
 * guessing inverts who owes what.
 *
 * 🔴 4. THE PREFILL WAS LOSSY. exchangePrefill drops a side item it cannot find
 * in the collection and records that it did. Sending a set known to be short
 * would post terms the visitor never agreed to.
 *
 * 🔴 5. THE ANCHOR CANNOT BE RECOVERED. Taken from the parent — the third
 * segment of `d:<counterpartId>:<listingId>` — and NEVER from the selection.
 * The server accepts the counter link only when the anchor matches the parent's;
 * when it does not it logs `offer.create.parentOffer.invalid` and creates a
 * plain offer anyway. Another silent 201.
 *
 * 🪤 `requestedItems` EXCLUDES THE ANCHOR. The feed synthesises the primary from
 * `offers.listing_id`, so including it there would double it on the card — the
 * same rule mobile's exchange.tsx follows.
 */

import { installDealThread, refreshDeals } from "./floorTransport";
import { offerTypeFor, type CounterPayload, type WireItem } from "./dealCounter";
import { SIGNIN_MESSAGE, UNRESOLVED_OFFER_MESSAGE } from "./dealActions";
import {
  buildNewOfferPayload,
  newAttemptId,
  offerSentMessage,
  readBuyerAddress,
} from "./makeOffer";

export const EX_SEND_SELECTOR = "#ex-send";

/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Couldn't send that counter — please try again";
/**
 * Nothing of theirs in the selection is a real listing, so there is no anchor.
 * ALSO the sentence a reloaded Exchange gets, because a reload empties the
 * decks (see the header) — which is what keeps the old refusal 1's protection
 * after the split. It reads correctly for both: with an empty deck the visitor
 * has nothing of theirs picked, whatever put them there.
 */
export const NO_ANCHOR_MESSAGE = "Pick at least one of their items to trade for";
/** Their side is picked but ours is empty — see sendFresh. */
export const NOTHING_OFFERED_MESSAGE = "Add something of yours, or cash, before sending";
/**
 * A side was SHORTENED between picking and sending, so the payload no longer
 * matches what the visitor agreed to. Found by TB on PR #937: LISTINGS is
 * refilled in place on every transport refresh while exch.theirs is an
 * independent Set of ids, and the review modal is rendered once and never
 * re-gated — only the review button is ever disabled, never the send.
 * ⚠️ `lossy` does NOT cover this. It reports what the PREFILL dropped when
 * building a counter, and a fresh offer has no prefill.
 */
export const SHORT_SELECTION_MESSAGE =
  "This deal changed while you were reviewing it — reopen it so you can see what you're sending";
/** Kept for the counter path — see refusal 1. Now reached only when a counter
 *  IS in progress and its parent will not resolve, never for a plain reload. */
export const LOST_COUNTER_MESSAGE =
  "This counter lost its link to the original offer — reopen it from the deal and your changes will send";
/** The prefill could not represent one of your side's items — see refusal 4. */
export const LOSSY_PREFILL_MESSAGE =
  "Part of this deal isn't in your collection, so it can't be countered here yet — reply in the thread instead";
export const COUNTERED_MESSAGE = "Counter sent";

interface ExchangeWindow extends Window {
  toast?: (message: string) => void;
  __gbzExchSides?: () => {
    mine: WireItem[];
    theirs: WireItem[];
    myCash: number;
    theirCash: number;
    counterOf: string | null;
    note: string;
    lossy: boolean;
    /** Selections the bridge could not resolve — see SHORT_SELECTION_MESSAGE. */
    droppedMine?: number;
    droppedTheirs?: number;
  } | null;
  __gbzThreadOfferId?: (threadId: string) => string | null;
  __gbzThreadIsBuyer?: (threadId: string) => boolean | null;
  __gbzClearThreadOfferId?: (threadId: string) => boolean;
  __gbzFinishExchSend?: () => boolean;
  __gbzListingRow?: (id: string) => { seller?: string } | null;
}

/**
 * The payload, as a pure function, so the direction mapping and the anchor rule
 * can be measured without a DOM.
 *
 * The decks are the viewer's; the wire is canonical buyer/seller. Everything
 * below is that one flip, applied to four fields at once.
 */
export function buildExchangePayload(input: {
  anchorListingId: string;
  parentOfferId: string;
  iAmBuyer: boolean;
  mine: WireItem[];
  theirs: WireItem[];
  myCash: number;
  theirCash: number;
  note?: string;
}): CounterPayload {
  const buyerItems = input.iAmBuyer ? input.mine : input.theirs;
  const sellerItems = input.iAmBuyer ? input.theirs : input.mine;
  const buyerCash = input.iAmBuyer ? input.myCash : input.theirCash;
  const sellerCash = input.iAmBuyer ? input.theirCash : input.myCash;

  /* The anchor is already the primary listing; the feed synthesises it from
     offers.listing_id, so repeating it here would show it twice on the card. */
  const requestedItems = sellerItems.filter((it) => it.listingId !== input.anchorListingId);

  const payload: CounterPayload = {
    listingId: input.anchorListingId,
    parentOfferId: input.parentOfferId,
    offerType: offerTypeFor(buyerItems, buyerCash),
  };
  if (buyerItems.length) payload.offeredItems = buyerItems;
  if (requestedItems.length) payload.requestedItems = requestedItems;
  // > 0 on both, the single guard that keeps a negative or an empty leg off the
  // wire — see dealCounter, where the same rule is documented at length.
  if (buyerCash > 0) payload.cashAmount = buyerCash;
  if (sellerCash > 0) payload.requestedCashAmount = sellerCash;
  if (input.note) payload.message = input.note;
  return payload;
}

export function installExchangeSend(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as ExchangeWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  let busy = false;

  async function send(payload: CounterPayload, threadId: string): Promise<void> {
    const res = await authFetch("/api/offers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.status === 401) {
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok) {
      /* The server's own words. The ones this path actually produces:
         "This counter has the exact same terms as the offer it's countering",
         "Cash can only ride one side of a trade", "This listing does not accept
         that offer type", and the per-item cash-only refusal, which names the
         item. A zod rejection puts the first message on errors[]. */
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        errors?: Array<{ message?: string }>;
      };
      notify(body.errors?.[0]?.message ?? body.error ?? GENERIC_FAILURE_MESSAGE);
      return;
    }

    /* Close the Exchange and go where his own flow goes — the deals list, where
       the countered row now lives. Then the truth from the server. */
    (win as ExchangeWindow).__gbzFinishExchSend?.();
    await refreshDeals(win, authFetch, myUserId).catch(() => null);
    const reread = await installDealThread(win, authFetch, threadId, myUserId).catch(() => false);
    /* A counter mints a NEW head offer, so the stashed id is stale the moment
       this lands. If the re-read did not refresh it, forget it — the same rule
       dealCounter follows, and for the same reason: a second counter posting
       parentOfferId at a superseded offer is created anyway, orphaned. */
    if (!reread) (win as ExchangeWindow).__gbzClearThreadOfferId?.(threadId);

    notify(COUNTERED_MESSAGE);
  }

  /**
   * A FRESH Exchange offer — no parent. Until card 1218886330579015 this took
   * refusal 1 and the Exchange could not start a deal on the website at all.
   *
   * 🔑 THE CREATOR IS THE CANONICAL BUYER, so unlike the counter path there is
   * no side to resolve. POST /api/offers pins `fromUserId` to whoever creates
   * the offer, which means `mine` IS the offered side and `theirs` IS the
   * requested side by construction. `__gbzThreadIsBuyer` is not consulted and
   * must not be: there is no thread to read it off, and defaulting it would
   * invert who owes what.
   *
   * 🔑 THE ANCHOR IS THE FIRST OF THEIR ITEMS THAT IS A REAL LISTING, the same
   * rule mobile's exchange.tsx follows (`sellerItems.find(i => !!i.listingId)`,
   * exchange.tsx:334). The offers route models `listingId` as the primary
   * requested item and `requestedItems` as extras BEYOND it, so the anchor is
   * filtered back out of the requested array by buildNewOfferPayload.
   *
   * 🔴 NO ANCHOR IS A REFUSAL, AND IT IS ALSO WHAT REPLACES REFUSAL 1. A
   * reloaded Exchange comes back with empty decks (app.js:8008 rebuilds via
   * `freshExch(r.handle)` with no opts), so a lost counter has nothing of
   * theirs picked and lands here rather than posting. The protection survives
   * the split; only the mechanism changed, from "no parent" to "nothing to
   * trade for".
   */
  async function sendFresh(
    sides: NonNullable<ReturnType<NonNullable<ExchangeWindow["__gbzExchSides"]>>>,
  ): Promise<void> {
    /* 🔴 A SHORTENED SIDE IS REFUSED BEFORE THE ANCHOR IS EVEN LOOKED FOR,
       because the anchor check cannot see this: it only catches the case where
       EVERYTHING of theirs vanished. A PARTIAL drop still resolves an anchor and
       would post a deal the visitor never agreed to. Checked first for the same
       reason refusal 4 is — it is the one the visitor can act on. */
    if ((sides.droppedMine ?? 0) > 0 || (sides.droppedTheirs ?? 0) > 0) {
      notify(SHORT_SELECTION_MESSAGE);
      return;
    }

    const anchor = sides.theirs.find((it) => !!it.listingId);
    if (!anchor?.listingId) {
      notify(NO_ANCHOR_MESSAGE);
      return;
    }
    const anchorListingId = anchor.listingId;

    /* 🔴 NOTHING ON MY SIDE IS NOT AN OFFER, and the server will not catch it.
       With no items and no cash, offerTypeFor returns "cash" and the `> 0`
       guard omits cashAmount — so this would post a coherent, accepted,
       ZERO-DOLLAR cash offer for someone's item, and the seller would see a
       real offer for nothing. The counter path cannot reach this state because
       it re-states both sides from the parent row; a fresh compose can, by
       picking only from their side. Refused out loud. */
    if (sides.mine.length === 0 && sides.myCash <= 0) {
      notify(NOTHING_OFFERED_MESSAGE);
      return;
    }

    const w = win as ExchangeWindow;
    const sellerHandle = w.__gbzListingRow?.(anchorListingId)?.seller ?? null;
    const buyerShippingAddressJson = await readBuyerAddress(authFetch);

    const payload = buildNewOfferPayload({
      listingId: anchorListingId,
      offeredItems: sides.mine,
      requestedItems: sides.theirs,
      buyerCash: sides.myCash,
      sellerCash: sides.theirCash,
      message: sides.note || undefined,
      buyerShippingAddressJson,
      /* Advisory only — the route persists 'exchange' for a multi-item offer
         regardless. Sent honestly because this one really is the Exchange. */
      origin: "exchange",
    });

    const res = await authFetch("/api/offers", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": newAttemptId() },
      body: JSON.stringify(payload),
    });

    if (res.status === 401) {
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok) {
      /* The server's own words. This path adds one the counter path cannot
         produce: the one-pending-offer 409, which is what catches a visitor
         who already has a live offer on the anchor. */
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        errors?: Array<{ message?: string }>;
      };
      notify(body.errors?.[0]?.message ?? body.error ?? GENERIC_FAILURE_MESSAGE);
      return;
    }

    /* Same close-then-truth order as the counter: his own finish takes the
       visitor to the deals list, where the new row now lives. No thread re-read
       — there is no parent thread to re-point, and refreshDeals brings the new
       row in on its own. */
    w.__gbzFinishExchSend?.();
    await refreshDeals(win, authFetch, myUserId).catch(() => null);
    notify(offerSentMessage(sellerHandle));
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    if (!target.closest(EX_SEND_SELECTOR)) return;

    const w = win as ExchangeWindow;
    const sides = w.__gbzExchSides?.() ?? null;
    if (!sides) return;

    /* Refusal 4 first for a fresh offer as well as a counter: a short prefill
       is terms the visitor never agreed to either way. */
    if (sides.lossy) {
      notify(LOSSY_PREFILL_MESSAGE);
      return;
    }

    /* NO PARENT: a FRESH Exchange offer. This used to take refusal 1 together
       with the reload; see the header for why separating them is safe, and why
       a reload still cannot post. */
    if (!sides.counterOf) {
      if (busy) return;
      busy = true;
      void sendFresh(sides)
        .catch(() => notify(GENERIC_FAILURE_MESSAGE))
        .finally(() => {
          busy = false;
        });
      return;
    }

    const threadId = sides.counterOf;
    const parentOfferId = w.__gbzThreadOfferId?.(threadId) ?? null;
    const iAmBuyer = w.__gbzThreadIsBuyer?.(threadId) ?? null;
    // Refusal 5 — the anchor comes from the parent, never from the selection.
    const anchorListingId = threadId.startsWith("d:") ? threadId.split(":")[2] ?? null : null;

    // Refusals 2, 3 and 5.
    if (!parentOfferId || iAmBuyer === null || !anchorListingId) {
      notify(UNRESOLVED_OFFER_MESSAGE);
      return;
    }

    if (busy) return;
    busy = true;
    const payload = buildExchangePayload({
      anchorListingId,
      parentOfferId,
      iAmBuyer,
      mine: sides.mine,
      theirs: sides.theirs,
      myCash: sides.myCash,
      theirCash: sides.theirCash,
      note: sides.note || undefined,
    });
    void send(payload, threadId)
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => {
        busy = false;
      });
  };

  /* Capture on the document, after the guard, not stopping propagation — the
     guard kills his fabricator and stays the fail-safe. #ex-send stays in
     GUARDED_WRITES: his handler paints a success screen and rewrites both sides
     of the thread from the deck with a hardcoded timestamp, none of which may
     run beside a real POST. */
  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
